import { ActionFunctionArgs, type MetaFunction } from "@remix-run/node";
import { useLoaderData } from "@remix-run/react";
import { Container } from "~/components/Container";
import { Layout } from "~/components/Layout";
import { Dew } from "~/components/Dew";
import { useState, useEffect } from "react";
import { FullscreenLoader } from "~/components/FullscreenLoader";
import { DEMO_COLLECTIONS, TODAY_DEMO_SITES } from "~/lib/demoDummyData";
import { eq } from "drizzle-orm";
import { db } from "~/database/db";
import { userTable } from "~/database/schema";
import { getCookieAuth } from "~/lib/.server/cookie";
import { createDemoMeta } from "~/lib/meta";

export const loader = async ({ request }: ActionFunctionArgs) => {
  let user;
  const auth = await getCookieAuth(request);
  if (auth) {
    user = await db.query.userTable.findFirst({
      where: eq(userTable.id, auth.userId),
    });
  }

  // Generate 2 days of dews (today and yesterday)
  const dews = [];
  const now = new Date();

  for (let i = 0; i < 2; i++) {
    const date = new Date(now);
    date.setDate(date.getDate() - i);

    dews.push({
      id: `demo-dew-${i + 1}`,
      generatedAt: date.toISOString(),
      userId: "demo-user"
    });
  }

  // Use imported demo collections
  const collections = DEMO_COLLECTIONS;

  // Shuffle function with deterministic seed based on today's date
  function shuffleArray<T>(array: T[], seed: number): T[] {
    const shuffled = [...array];
    let random = seed;

    for (let i = shuffled.length - 1; i > 0; i--) {
      // Simple linear congruential generator for deterministic random
      random = (random * 1664525 + 1013904223) % Math.pow(2, 32);
      const j = Math.floor((random / Math.pow(2, 32)) * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }

    return shuffled;
  }

  // Create a seed based on today's date to ensure consistent order per day
  const today = new Date();
  const seed = today.getFullYear() * 10000 + (today.getMonth() + 1) * 100 + today.getDate();

  // Shuffle demo sites to mix collections
  const demoSites = shuffleArray(TODAY_DEMO_SITES, seed);

  // Add cover images and creation dates
  const raindrops = demoSites.map((site, index) => {
    const createdDate = new Date(now);
    createdDate.setDate(createdDate.getDate() - Math.floor(Math.random() * 30)); // Random date within last 30 days

    return {
      raindropId: 10000 + index,
      collectionId: site.collectionId,
      cover: site.cover, // Use cover from JSON data
      link: site.link,
      title: site.title,
      created: createdDate.toISOString(),
      domain: site.domain,
      excerpt: site.excerpt,
      tags: []
    };
  });

  const currentDew = dews[0];

  return {
    user,
    dew: currentDew,
    dews,
    raindrops,
    collections
  };
};

export const meta: MetaFunction<typeof loader> = () => createDemoMeta();

export default function Demo() {
  const { user, dews, dew, raindrops, collections } = useLoaderData<typeof loader>();
  const [loading, setLoading] = useState(true);

  // Simulate loading time for demo purposes
  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  return (
    <Layout user={user}>
      <FullscreenLoader isLoading={loading} />
      <Container>
        {!loading && (
          <>
            {/* Demo Notice */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-blue-800">
                    Demo Mode
                  </h3>
                  <div className="mt-1 text-sm text-blue-700">
                    <p>You&apos;re viewing a demo with sample data. <a href="/signin" className="underline hover:no-underline">Sign in with your Raindrop.io account</a> to experience Dewdrop with your real bookmarks.</p>
                  </div>
                </div>
              </div>
            </div>

            <Dew
              dews={dews}
              raindrops={raindrops}
              currentDew={dew}
              collections={collections}
              isDemo={true}
            />
          </>
        )}
      </Container>
    </Layout>
  );
}